/**
 * Reduz a foto ANTES de subir.
 *
 * Comprovante é foto de tela de banco: o que precisa sobreviver é o texto —
 * valor, data, nome, os quatro últimos dígitos. Uma foto de celular chega com
 * 3 a 4 MB e 4000px de largura para carregar isso. A 1600px e 70% de
 * qualidade ela vira 250 a 400 KB, continua legível na tela e no zoom, e o
 * bucket dura dez anos em vez de dois.
 *
 * A conta que motiva: 17 parcelas por mês, uma foto cada, 3 MB. São 600 MB por
 * ano contra 1 GB de cota. Comprimindo, 60 MB.
 *
 * NADA DISSO É OBRIGATÓRIO. Toda falha aqui devolve o arquivo original: um
 * comprovante grande que sobe é melhor que um comprovante pequeno que não
 * existe. Só o PDF passa sem ser tocado — ele já é o documento do banco, e
 * rasterizá-lo destruiria o texto selecionável.
 */

/** Só imagem raster que o canvas sabe desenhar. PDF e HEIC ficam de fora. */
const COMPRIME = ['image/jpeg', 'image/png', 'image/webp'];

/** O que sai da compressão. JPEG porque é o menor para foto com texto. */
const SAIDA = 'image/jpeg';

export const LARGURA_MAXIMA = 1600;
export const QUALIDADE = 0.7;

/**
 * O tamanho de destino: teto na LARGURA, e nunca aumentar.
 *
 * O teto é na largura e não no maior lado, e a diferença aparece no caso mais
 * comum de todos. O print do pix num celular vem 1170x2532 — em pé. Limitando
 * o maior lado, ele desceria para 739x1600: o comprovante perderia 37% da
 * largura, que é justamente a direção em que o texto se lê. Limitando a
 * largura, ele passa intacto, e a foto de 4000x3000 da nota impressa ainda
 * encolhe para 1600x1200, que é o que essa conta veio resolver.
 *
 * E nunca esticar: aumentar só cria pixel inventado e devolve arquivo maior
 * que o original.
 */
export const dimensoesReduzidas = (largura, altura, maxima = LARGURA_MAXIMA) => {
  if (!largura || largura <= maxima) return { largura, altura };
  const fator = maxima / largura;
  return {
    largura: maxima,
    altura: Math.round(altura * fator),
  };
};

/**
 * `createImageBitmap` e não `<img>`, por causa da ORIENTAÇÃO.
 *
 * Foto tirada com o celular deitado carrega a rotação no EXIF, e o
 * `drawImage` de um `<img>` ignora isso — o comprovante subiria de lado, o
 * que é pior que subir pesado. `imageOrientation: 'from-image'` resolve na
 * origem. Onde a função não existe, não comprimimos: melhor grande e em pé.
 */
const podeComprimir = (arquivo) =>
  typeof createImageBitmap === 'function'
  && typeof document !== 'undefined'
  && COMPRIME.includes(arquivo?.type);

const paraBlob = (canvas, tipo, qualidade) => new Promise((resolve) => {
  if (typeof canvas.toBlob !== 'function') { resolve(null); return; }
  canvas.toBlob(resolve, tipo, qualidade);
});

/**
 * Devolve uma versão menor do arquivo, ou o próprio arquivo.
 *
 * Devolver o ORIGINAL quando a "compressão" engordou é parte da regra, não
 * desistência: print de tela já sai comprimido do sistema, e recodificá-lo em
 * JPEG costuma somar bytes. Quem chama não precisa saber qual dos dois veio.
 */
export const comprimirImagem = async (arquivo, {
  larguraMaxima = LARGURA_MAXIMA, qualidade = QUALIDADE,
} = {}) => {
  if (!arquivo || !podeComprimir(arquivo)) return arquivo;

  try {
    const bitmap = await createImageBitmap(arquivo, { imageOrientation: 'from-image' });
    const { largura, altura } = dimensoesReduzidas(bitmap.width, bitmap.height, larguraMaxima);

    const canvas = document.createElement('canvas');
    canvas.width = largura;
    canvas.height = altura;
    const ctx = canvas.getContext('2d');
    if (!ctx) return arquivo;
    // Fundo branco: PNG com transparência viraria preto no JPEG, e um
    // comprovante com fundo preto é ilegível.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, largura, altura);
    ctx.drawImage(bitmap, 0, 0, largura, altura);
    bitmap.close?.();

    const blob = await paraBlob(canvas, SAIDA, qualidade);
    if (!blob || blob.size >= arquivo.size) return arquivo;

    const nome = String(arquivo.name || 'comprovante').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], nome, { type: SAIDA, lastModified: Date.now() });
  } catch (e) {
    // Formato que o navegador não decodifica, memória curta num celular
    // antigo, canvas sujo: em todos, o original ainda serve.
    console.warn('[imagem] não deu para reduzir, subindo o original:', e?.message);
    return arquivo;
  }
};
